// @flow
import {ofType, StateObservable} from 'redux-observable';
// import {EMPTY} from 'rxjs';
import {debounceTime, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {buildRoute} from '../../http';
// import type {AjaxError} from 'rxjs/ajax';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';

import {LoginSuccess, GetSessionSuccess} from '../auth/duck';
import {
  getStoreInformationApi,
  getResellerInformationApi,
  endpoints,
  saveFacebookIntegrationApi,
  saveDowngradeInformationApi,
  getStoreProfileInformationApi,
  getStoreProductSettingApi,
  saveStoreProductSettingApi,
} from './api';

import type {
  StoreInformation,
  StoreProfileInformation,
  ResellerInformation,
} from '../../entities';
import {
  SaveGeneralInformationSuccess,
  SaveStoreProfileInformationSuccess,
} from '../configure/general/duck';
import type {RootState} from '..';
import {getFacebookIntegrationApi} from './api';
import {of} from 'rxjs';
import {FetchStoreSuccess} from '../store/duck';
import {SaveStoreInformationSuccess} from '../my-store/customize/duck';

export type StoreProductSetting = {
  enabledLiveViews: boolean,
  enabledSales: boolean,
};
export type FacebookIntegration = {
  validationToken?: string,
  pixelId?: string,
};

export type UiAction = Action & {
  storeInformation?: StoreInformation,
  storeProfileInformation?: StoreProfileInformation,
  resellerInformation?: ResellerInformation,
  error?: string,
  resellerId?: string,
  saveFacebookIntegrationRequest?: {
    validationToken?: string,
    pixelId?: string,
  },
  saveDowngradeInformationRequest?: {
    whyNotRenew: string,
    whatLikedMost: string,
    suggestion?: string,
  },
};

export type UiState = {
  storeInformation?: StoreInformation,
  isFetchingStoreInformation?: boolean,
  storeProfileInformation?: StoreProfileInformation,
  isFetchingStoreProfileInformation?: boolean,
  fetchStoreProfileInformationError?: string,
  resellerInformation?: ResellerInformation,
  isFetchingResellerInformation?: boolean,
  fetchStoreInformationError?: string,
  fetchResellerInformationError?: string,
  facebookIntegration?: FacebookIntegration,
  isFetchingFacebookIntegration?: boolean,
  saveFacebookIntegrationSuccess?: boolean,
  isSavingDowngradeInformation?: boolean,
  saveDowngradeInformationSuccess?: boolean,
  saveDowngradeInformationError?: boolean,
  tier22ModalOpen?: boolean,
  noUserPermissionsModalOpen?: boolean,
  storeProductSetting?: StoreProductSetting,
  isSavingProductSetting?: boolean,
  saveProductSettingSuccess?: boolean,
  saveProductSettingError?: boolean,
};

export class FetchStoreInformation extends ActionCreator<UiAction> {}
export class FetchStoreInformationSuccess extends ActionCreator<UiAction> {}
export class FetchStoreInformationErrorFn extends ActionCreator<UiAction> {}

export class FetchStoreProfileInformation extends ActionCreator<UiAction> {}
export class FetchStoreProfileInformationSuccess extends ActionCreator<UiAction> {}
export class FetchStoreProfileInformationErrorFn extends ActionCreator<UiAction> {}

export class FetchResellerInformation extends ActionCreator<UiAction> {}
export class FetchResellerInformationSuccess extends ActionCreator<UiAction> {}
export class FetchResellerInformationErrorFn extends ActionCreator<UiAction> {}

export class SaveFacebookIntegration extends ActionCreator<UiAction> {}
export class SaveFacebookIntegrationSuccess extends ActionCreator<UiAction> {}
export class SaveFacebookIntegrationErrorFn extends ActionCreator<UiAction> {}

export class FetchFacebookIntegration extends ActionCreator<UiAction> {}
export class FetchFacebookIntegrationSuccess extends ActionCreator<UiAction> {}
export class FetchFacebookIntegrationErrorFn extends ActionCreator<UiAction> {}

export class SaveDowngradeInformation extends ActionCreator<UiAction> {}
export class SaveDowngradeInformationSuccess extends ActionCreator<UiAction> {}
export class SaveDowngradeInformationErrorFn extends ActionCreator<UiAction> {}
export class ResetDowngradeInformationFn extends ActionCreator<UiAction> {}

export class FetchStoreProductSetting extends ActionCreator<UiAction> {}
export class FetchStoreProductSettingSuccess extends ActionCreator<UiAction> {}
export class FetchStoreProductSettingErrorFn extends ActionCreator<UiAction> {}

export class SaveStoreProductSetting extends ActionCreator<UiAction> {}
export class SaveStoreProductSettingSuccess extends ActionCreator<UiAction> {}
export class SaveStoreProductSettingErrorFn extends ActionCreator<UiAction> {}

export class OpenTier22Modal extends ActionCreator<UiAction> {}
export class CloseTier22Modal extends ActionCreator<UiAction> {}
export class OpenNoUserPermissionsModal extends ActionCreator<UiAction> {}
export class CloseNoUserPermissionsModal extends ActionCreator<UiAction> {}

export const actions = {
  fetchStoreInformation: () => new FetchStoreInformation(),
  // fetchResellerInformation: (payload: UiAction) =>
  //   new FetchResellerInformation(payload),
  saveProductSetting: (payload: UiAction) =>
    new SaveStoreProductSetting(payload),
  fetchStoreProfileInformation: () => new FetchStoreProfileInformation(),
  saveFacebookIntegration: (payload: UiAction) =>
    new SaveFacebookIntegration(payload),
  fetchFacebookIntegration: () => new FetchFacebookIntegration(),
  saveDowngradeInformation: (payload: UiAction) =>
    new SaveDowngradeInformation(payload),
  resetDowngradeInformation: () => new ResetDowngradeInformationFn(),
  openTier22Modal: () => new OpenTier22Modal(),
  closeTier22Modal: () => new CloseTier22Modal(),
  openNoUserPermissionsModal: () => new OpenNoUserPermissionsModal(),
  closeNoUserPermissionsModal: () => new CloseNoUserPermissionsModal(),
};

export const initialState = {
  isFetchingStoreInformation: true,
  isFetchingResellerInformation: true,
};

export default createReducer<UiState>(initialState, {
  [FetchStoreInformation.type]: (state: UiState) => ({
    ...state,
    isFetchingStoreInformation: true,
  }),
  [FetchStoreInformationSuccess.type]: (state: UiState, action: UiAction) => ({
    ...state,
    isFetchingStoreInformation: undefined,
    storeInformation: action.storeInformation,
  }),
  [FetchStoreInformationErrorFn.type]: (state: UiState, action: UiAction) => ({
    ...state,
    isFetchingStoreInformation: undefined,
    error: action.error,
  }),
  [SaveGeneralInformationSuccess.type]: (state: UiState, action: UiAction) => ({
    ...state,
    storeInformation: action.information,
  }),
  [FetchStoreProfileInformation.type]: state => ({
    ...state,
    isFetchingStoreProfileInformation: true,
  }),
  [FetchStoreProfileInformationSuccess.type]: (state, action: UiAction) => ({
    ...state,
    isFetchingStoreProfileInformation: undefined,
    storeProfileInformation: action.storeProfileInformation,
  }),
  [FetchStoreProfileInformationErrorFn.type]: (state, action: UiAction) => ({
    ...state,
    isFetchingStoreProfileInformation: undefined,
    fetchStoreProfileInformationError: action.error,
  }),
  [SaveStoreProfileInformationSuccess.type]: (state, action: UiAction) => ({
    ...state,
    storeProfileInformation: action.storeProfileInformation,
  }),
  [FetchResellerInformation.type]: state => ({
    ...state,
    isFetchingResellerInformation: true,
  }),
  [FetchResellerInformationSuccess.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isFetchingResellerInformation: undefined,
    resellerInformation: action.resellerInformation,
  }),
  [FetchResellerInformationErrorFn.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isFetchingResellerInformation: undefined,
    error: action.error,
  }),
  [SaveFacebookIntegration.type]: state => ({
    ...state,
    isSavingFacebokIntegration: true,
    saveFacebookIntegrationSuccess: undefined,
  }),
  [SaveFacebookIntegrationSuccess.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isSavingFacebokIntegration: undefined,
    saveFacebookIntegrationSuccess: true,
    facebookIntegration: action.facebookIntegration,
  }),
  [SaveFacebookIntegrationErrorFn.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isSavingFacebokIntegration: undefined,
    error: action.error,
  }),
  [FetchFacebookIntegration.type]: state => ({
    ...state,
    isFetchingFacebookIntegration: true,
  }),
  [FetchFacebookIntegrationSuccess.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isFetchingFacebookIntegration: undefined,
    facebookIntegration: action.facebookIntegration,
  }),
  [FetchFacebookIntegrationErrorFn.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isFetchingFacebookIntegration: undefined,
    error: action.error,
  }),
  [FetchStoreProductSettingSuccess.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    storeProductSetting: action.data,
  }),
  [SaveStoreProductSetting.type]: (state: UiState, action: UiAction) => ({
    ...state,
    isSavingProductSetting: true,
    saveProductSettingSuccess: undefined,
    saveProductSettingError: undefined,
  }),
  [SaveStoreProductSettingSuccess.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isSavingProductSetting: undefined,
    saveProductSettingSuccess: true,
    saveProductSettingError: undefined,
    storeProductSetting: action.data,
  }),
  [SaveStoreProductSettingErrorFn.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isSavingProductSetting: undefined,
    saveProductSettingSuccess: undefined,
    saveProductSettingError: action.error,
  }),
  [SaveDowngradeInformation.type]: (state: UiState) => ({
    ...state,
    isSavingDowngradeInformation: true,
    saveDowngradeInformationSuccess: undefined,
    saveDowngradeInformationError: undefined,
  }),
  [SaveDowngradeInformationSuccess.type]: (state: UiState) => ({
    ...state,
    isSavingDowngradeInformation: false,
    saveDowngradeInformationSuccess: true,
    saveDowngradeInformationError: undefined,
  }),
  [SaveDowngradeInformationErrorFn.type]: (
    state: UiState,
    action: UiAction
  ) => ({
    ...state,
    isSavingDowngradeInformation: false,
    saveDowngradeInformationSuccess: false,
    saveDowngradeInformationError: action.error,
  }),
  [OpenTier22Modal.type]: (state: UiState) => ({
    ...state,
    tier22ModalOpen: true,
  }),
  [CloseTier22Modal.type]: (state: UiState, action: UiAction) => ({
    ...state,
    tier22ModalOpen: false,
  }),
  [OpenNoUserPermissionsModal.type]: (state: UiState) => ({
    ...state,
    noUserPermissionsModalOpen: true,
  }),
  [CloseNoUserPermissionsModal.type]: (state: UiState, action: UiAction) => ({
    ...state,
    noUserPermissionsModalOpen: false,
  }),
  [ResetDowngradeInformationFn.type]: (state: UiState) => ({
    ...state,
    isSavingDowngradeInformation: undefined,
    saveDowngradeInformationSuccess: undefined,
    saveDowngradeInformationError: undefined,
  }),
});

export function fetchStoreInformationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchStoreInformation.type,
      SaveStoreInformationSuccess.type,
      GetSessionSuccess.type,
      LoginSuccess.type
    ),
    switchMap(() =>
      getStoreInformationApi().pipe(
        map(
          (storeInformation: StoreInformation) =>
            new FetchStoreInformationSuccess({storeInformation})
        ),
        buildCatchError(FetchStoreInformationErrorFn)
      )
    )
  );
}

export function fetchStoreProfileInformationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchStoreProfileInformation.type),
    switchMap(() =>
      getStoreProfileInformationApi().pipe(
        map(
          (storeProfileInformation: StoreProfileInformation) =>
            new FetchStoreProfileInformationSuccess({storeProfileInformation})
        ),
        buildCatchError(FetchStoreProfileInformationErrorFn)
      )
    )
  );
}

export function fetchResellerInformationEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(LoginSuccess.type, FetchStoreSuccess.type),
    // filter(({store}: StoreAction) => store && store.resellerId),
    switchMap(() => {
      const resellerId =
        state$.value &&
        state$.value.store &&
        state$.value.store.store &&
        state$.value.store.store.resellerId
          ? state$.value.store.store.resellerId
          : '';
      if (resellerId)
        return getResellerInformationApi(undefined, {
          route: buildRoute(endpoints.GET_RESELLER_INFORMATION, [resellerId]),
        }).pipe(
          map(
            (resellerInformation: ResellerInformation) =>
              new FetchResellerInformationSuccess({resellerInformation})
          ),
          buildCatchError(FetchResellerInformationErrorFn)
        );
      else return new of(new FetchResellerInformationErrorFn());
    })
  );
}

export function saveFacebookIntegrationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveFacebookIntegration.type),
    debounceTime(250),
    switchMap(({saveFacebookIntegrationRequest}: UiAction) => {
      return saveFacebookIntegrationApi(saveFacebookIntegrationRequest).pipe(
        map(
          (facebookIntegration: FacebookIntegration) =>
            new SaveFacebookIntegrationSuccess({facebookIntegration})
        ),
        buildCatchError(SaveFacebookIntegrationErrorFn)
      );
    })
  );
}

export function fetchFacebookIntegrationEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchFacebookIntegration.type),
    switchMap(() => {
      const storeId =
        state$.value &&
        state$.value.store &&
        state$.value.store.store &&
        state$.value.store.store.id
          ? state$.value.store.store.id
          : '';
      return getFacebookIntegrationApi(undefined, {
        route: buildRoute(endpoints.FETCH_FACEBOOK_INTEGRATION, [storeId]),
      }).pipe(
        map(
          (facebookIntegration: FacebookIntegration) =>
            new FetchFacebookIntegrationSuccess({facebookIntegration})
        ),
        buildCatchError(FetchFacebookIntegrationErrorFn)
      );
    })
  );
}

export function saveDowngradeInformationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveDowngradeInformation.type),
    switchMap(({saveDowngradeInformationRequest}: UiAction) =>
      saveDowngradeInformationApi({...saveDowngradeInformationRequest}).pipe(
        map(
          (data: StoreInformation) =>
            new SaveDowngradeInformationSuccess({data})
        ),
        buildCatchError(SaveDowngradeInformationErrorFn)
      )
    )
  );
}

export function getStoreProductSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(LoginSuccess.type, GetSessionSuccess.type),
    switchMap(() =>
      getStoreProductSettingApi().pipe(
        map(
          (data: StoreProductSetting) =>
            new FetchStoreProductSettingSuccess({data})
        ),
        buildCatchError(FetchStoreProductSettingErrorFn)
      )
    )
  );
}

export function saveStoreProductSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveStoreProductSetting.type),
    switchMap(({productSettingRequest}) =>
      saveStoreProductSettingApi(productSettingRequest).pipe(
        map(
          (data: StoreProductSetting) =>
            new SaveStoreProductSettingSuccess({data})
        ),
        buildCatchError(SaveStoreProductSettingErrorFn)
      )
    )
  );
}
